"use client";

import { useState, useEffect } from "react";
import { usePushSubscription } from "@/lib/hooks/usePushSubscription";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";

const STORAGE_KEY = "push-prompt-dismissed";

export function PushPrompt() {
  const [visible, setVisible] = useState(false);
  const { data: currentUser } = useCurrentUser();
  const { supported, permission, subscribe, loading } = usePushSubscription();

  useEffect(() => {
    if (!currentUser) return;
    if (!supported) return;
    if (permission !== "default") return;
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch {
      return;
    }
    const timer = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(timer);
  }, [currentUser, supported, permission]);

  function handleDismiss() {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  }

  async function handleEnable() {
    await subscribe();
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 lg:left-auto lg:right-4 lg:max-w-sm animate-fade-in">
      <div className="bg-surface rounded-2xl border border-border shadow-lg p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-muted/50 flex items-center justify-center shrink-0">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">Notifikationer</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Få besked om svar og aktivitet i dine grupper
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="p-2 -m-1 text-subtle-foreground hover:text-gray-600 dark:hover:text-gray-300 transition-colors shrink-0"
            aria-label="Luk"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={handleDismiss}
            className="flex-1 py-2 text-sm font-medium text-muted-foreground bg-muted rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            Ikke nu
          </button>
          <button
            onClick={handleEnable}
            disabled={loading}
            className="flex-1 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            Ja tak
          </button>
        </div>
      </div>
    </div>
  );
}
